<form [formGroup]="configForm" class="admin-config-container">
    <!-- Header -->
    <div class="config-header">
        <div class="header-content">
            <h1 class="config-title">
                <fa-icon [icon]="icons.faCog" class="title-icon"></fa-icon>
                Configuration Management
            </h1>
            <p class="config-subtitle">Customize board features and user experience settings</p>
        </div>
    </div>

    <!-- Settings Grid -->
    <div class="settings-grid">
        <!-- Layout Preferences Card -->
        <div class="settings-card">
            <div class="card-header">
                <div class="header-left">
                    <div class="feature-icon layout-icon">
                        <fa-icon [icon]="icons.faThLarge"></fa-icon>
                    </div>
                    <div class="header-text">
                        <h2 class="feature-title">Layout Preferences</h2>
                        <p class="feature-description">Configure which sales channels are available on your board</p>
                    </div>
                </div>
            </div>

            <div class="card-body">
                <!-- Channel Enable Flags -->
                <div class="form-section">
                    <h5 class="section-title">Available Channels</h5>
                    
                    <div class="setting-item" *ngIf="instoreChannelPurchased">
                        <div class="setting-control">
                            <label for="enableInstoreChannel" class="toggle-label">Enable Walk-ins Channel</label>
                            <input 
                                type="checkbox" 
                                id="enableInstoreChannel"
                                formControlName="enableInstoreChannel"
                                (change)="saveConfig()"
                                class="checkbox-input">
                        </div>
                        <div class="setting-status">
                            <small class="text-muted">Show Walk-ins/Floor traffic column for in-store customers</small>
                        </div>
                    </div>

                    <div class="setting-item" *ngIf="instoreChannelPurchased && configForm.get('enableInstoreChannel')?.value !== false" [class.highlight-checkbox]="highlightDelivery">
                        <div class="setting-control">
                            <label for="deliveryToggle" class="toggle-label">Enable Delivery Features</label>
                            <input 
                                type="checkbox" 
                                id="deliveryToggle"
                                formControlName="showDelivery"
                                (change)="saveConfig()"
                                class="checkbox-input">
                        </div>
                        <div class="setting-status">
                            <small class="text-muted">Show delivery tracking column and enable delivery states for employees</small>
                        </div>
                    </div>

                    <div class="setting-item" *ngIf="!phoneChannelPurchased && !internetChannelPurchased">
                        <div class="setting-control">
                            <label for="useModernLayout" class="toggle-label">Use Modern Card-Based Layout</label>
                            <input 
                                type="checkbox" 
                                id="useModernLayout"
                                formControlName="useModernLayout"
                                (change)="saveConfig()"
                                class="checkbox-input">
                        </div>
                        <div class="setting-status">
                            <small class="text-muted">Display employees in a modern card-based layout instead of classic 5-column table</small>
                        </div>
                    </div>

                    <div class="setting-item" *ngIf="phoneChannelPurchased">
                        <div class="setting-control">
                            <label for="enablePhoneChannel" class="toggle-label">Enable Phone Channel</label>
                            <input 
                                type="checkbox" 
                                id="enablePhoneChannel"
                                formControlName="enablePhoneChannel"
                                (change)="saveConfig()"
                                class="checkbox-input">
                        </div>
                        <div class="setting-status">
                            <small class="text-muted">Show Phone Ups column for inbound phone call leads</small>
                        </div>
                    </div>

                    <div class="setting-item" *ngIf="internetChannelPurchased">
                        <div class="setting-control">
                            <label for="enableOnlineChannel" class="toggle-label">Enable Internet Channel</label>
                            <input 
                                type="checkbox" 
                                id="enableOnlineChannel"
                                formControlName="enableOnlineChannel"
                                (change)="saveConfig()"
                                class="checkbox-input">
                        </div>
                        <div class="setting-status">
                            <small class="text-muted">Show Internet Ups column for website/email leads</small>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Lead Assignment Rules Card (only shown when phone or internet channels are visible) -->
        <div class="settings-card" *ngIf="(phoneChannelPurchased && instoreChannelPurchased && configForm.get('enableInstoreChannel')?.value !== false && configForm.get('enablePhoneChannel')?.value) || (internetChannelPurchased && instoreChannelPurchased && configForm.get('enableInstoreChannel')?.value !== false && configForm.get('enableOnlineChannel')?.value)">
            <div class="card-header">
                <div class="header-left">
                    <div class="feature-icon channel-icon">
                        <fa-icon [icon]="icons.faUserCheck"></fa-icon>
                    </div>
                    <div class="header-text">
                        <h2 class="feature-title">Lead Assignment Rules</h2>
                        <p class="feature-description">Configure which queue positions can take leads from each channel</p>
                    </div>
                </div>
            </div>

            <div class="card-body">
                <!-- Phone Availability Rules (only shown when phone is purchased AND instore is purchased+enabled AND phone is enabled) -->
                <div class="form-section" *ngIf="phoneChannelPurchased && instoreChannelPurchased && configForm.get('enableInstoreChannel')?.value !== false && configForm.get('enablePhoneChannel')?.value">
                    <h5 class="section-title">Phone Channel - Who Can Take Leads?</h5>
                    
                    <div class="setting-item">
                        <div class="setting-control">
                            <label for="allowUpToTakePhone" class="toggle-label">
                                <fa-icon [icon]="icons.faUser"></fa-icon> "Up" Position Can Take Phone Leads
                            </label>
                            <input 
                                type="checkbox" 
                                id="allowUpToTakePhone"
                                formControlName="allowUpToTakePhone"
                                (change)="saveConfig()"
                                class="checkbox-input">
                        </div>
                    </div>

                    <div class="setting-item">
                        <div class="setting-control">
                            <label for="allowOnDeckToTakePhone" class="toggle-label">
                                <fa-icon [icon]="icons.faUser"></fa-icon> "On Deck" Position Can Take Phone Leads
                            </label>
                            <input 
                                type="checkbox" 
                                id="allowOnDeckToTakePhone"
                                formControlName="allowOnDeckToTakePhone"
                                (change)="saveConfig()"
                                class="checkbox-input">
                        </div>
                    </div>
                </div>

                <!-- Internet Availability Rules (only shown when internet is purchased AND instore is purchased+enabled AND online is enabled) -->
                <div class="form-section" *ngIf="internetChannelPurchased && instoreChannelPurchased && configForm.get('enableInstoreChannel')?.value !== false && configForm.get('enableOnlineChannel')?.value">
                    <h5 class="section-title">Internet Channel - Who Can Take Leads?</h5>
                    
                    <div class="setting-item">
                        <div class="setting-control">
                            <label for="allowUpToTakeInternet" class="toggle-label">
                                <fa-icon [icon]="icons.faUser"></fa-icon> "Up" Position Can Take Internet Leads
                            </label>
                            <input 
                                type="checkbox" 
                                id="allowUpToTakeInternet"
                                formControlName="allowUpToTakeInternet"
                                (change)="saveConfig()"
                                class="checkbox-input">
                        </div>
                    </div>

                    <div class="setting-item">
                        <div class="setting-control">
                            <label for="allowOnDeckToTakeInternet" class="toggle-label">
                                <fa-icon [icon]="icons.faUser"></fa-icon> "On Deck" Position Can Take Internet Leads
                            </label>
                            <input 
                                type="checkbox" 
                                id="allowOnDeckToTakeInternet"
                                formControlName="allowOnDeckToTakeInternet"
                                (change)="saveConfig()"
                                class="checkbox-input">
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Footer Note -->
    <div class="config-footer">
        <p><fa-icon [icon]="icons.faShieldAlt"></fa-icon> All changes are automatically saved and applied immediately</p>
    </div>
</form>
